class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();
    this.debounceTimer = null;
    this.abortController = null;

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    // Debounce: wait 150ms for rapid clicks to settle
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.dispatchEvent(new Event('submit', { cancelable: true }));
    }, 150);
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    // Cancel any pending request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    this.isRendering = true;
    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings && this.isRendering) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    this.isRendering = false;
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    if (!this.abortController) this.abortController = new AbortController();
    this.beforeRenderSection();

    fetch(url, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.renderFilters(responseText, event);
        this.renderFiltersActive(responseText);
        this.renderProductGridContainer(responseText);
        this.renderFacetFiltersContainer(responseText);
        this.renderProductCount(responseText);
        this.renderSortBy(responseText);
        this.renderSortByMobile(responseText);

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
        this.cachedMap.set(url, responseText);

        this.afterRenderSection();
      })
      .catch((error) => {
        // Ignore abort errors (expected when cancelling)
        if (error.name !== 'AbortError') {
          console.error('Facet fetch error:', error);
        }
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    // Minimal delay for cached responses (just enough for UI feedback)
    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderFacetFiltersContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 50);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    const facetElementsFormDom = document.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
    delete document.getElementById(id).dataset.loadedPage;

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderFacetFiltersContainer(responseText) {
    const id = 'FacetFiltersContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return Promise.resolve();
    this.loadingState();

    const url = this.setUrl();

    return fetch(url.toString())
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) productGridContainer.dataset.loadedPage = url.searchParams.get('page') || '';
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    if (!pagination) return;
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

const ListingScroll = {
  storageKey: 'listing-scroll',
  maxAge: 30 * 60 * 1000,
  clickedAt: 0,

  listingKey() {
    return window.location.pathname + window.location.search;
  },

  firstPage() {
    return Number(new URL(window.location.href).searchParams.get('page')) || 1;
  },

  pageTop(element) {
    let top = 0;
    for (let node = element; node; node = node.offsetParent) top += node.offsetTop;
    return top;
  },

  save(link) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const card = link.closest('.product-card') || link;
    const state = {
      key: this.listingKey(),
      href: link.getAttribute('href'),
      top: card.getBoundingClientRect().top,
      scrollY: window.scrollY,
      loadedPage: Number(container.dataset.loadedPage) || this.firstPage(),
      savedAt: Date.now(),
    };
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Listing scroll error:', error);
    }
  },

  saveCurrent() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const card = Array.from(container.querySelectorAll('.product-card')).find(
      (item) => item.getBoundingClientRect().bottom > 0
    );
    const link = card && card.querySelector('a[href*="/products/"]');
    if (link) this.save(link);
  },

  read() {
    try {
      const state = JSON.parse(sessionStorage.getItem(this.storageKey));
      if (state && state.key === this.listingKey() && Date.now() - state.savedAt < this.maxAge) return state;
    } catch (error) {
      console.error('Listing scroll error:', error);
    }
    return null;
  },

  cardTop(container, state) {
    const link = Array.from(container.querySelectorAll('a[href]')).find((item) => item.getAttribute('href') === state.href);
    const card = link && (link.closest('.product-card') || link);
    return Math.max(0, card ? this.pageTop(card) - state.top : state.scrollY);
  },

  async restore() {
    const navigation = performance.getEntriesByType('navigation')[0];
    const container = document.getElementById('ProductGridContainer');
    if (!navigation || navigation.type !== 'back_forward' || !container) return;

    const state = this.read();
    if (!state) return;

    let userMoved = false;
    const stop = () => {
      userMoved = true;
    };
    const userInputs = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    userInputs.forEach((type) => window.addEventListener(type, stop, { passive: true }));
    const stillValid = () => !userMoved && this.listingKey() === state.key;

    history.scrollRestoration = 'manual';
    document.body.removeAttribute('data-initializing');
    try {
      if (document.readyState !== 'complete') {
        await new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
      }

      const loadedPage = () => Number(container.dataset.loadedPage) || this.firstPage();
      for (let tries = 0; stillValid() && loadedPage() < state.loadedPage && tries < 100; tries += 1) {
        const button = container.querySelector('button[is="load-more-button"]');
        if (!button) break;
        if (button.classList.contains('btn--loading')) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        } else {
          await button.onClick();
        }
      }

      const deadline = Date.now() + 5000;
      let settled = 0;
      while (settled < 2 && stillValid() && Date.now() < deadline) {
        const top = this.cardTop(container, state);
        if (document.documentElement.scrollHeight - window.innerHeight >= top - 1) {
          window.scrollTo({ top, behavior: 'auto' });
          settled = Math.abs(window.scrollY - top) < 2 ? settled + 1 : 0;
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      if (settled === 0 && stillValid()) window.scrollTo({ top: this.cardTop(container, state), behavior: 'auto' });
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Listing scroll error:', error);
    } finally {
      userInputs.forEach((type) => window.removeEventListener(type, stop));
      history.scrollRestoration = 'auto';
    }
  },
};

const ListingReference = {
  sectionId: 'listing-counts',
  cardCountSectionId: 'listing-card-count',
  cardCountBatch: 4,
  pages: new Map(),
  cardCounts: new Map(),

  url(path, sectionId) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url;
  },

  fetchSource(url, selector, signal) {
    return fetch(url.toString(), { signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((responseText) => {
        const source = new DOMParser().parseFromString(responseText, 'text/html').querySelector(selector);
        if (!source) throw new Error(`missing ${selector}`);
        return source;
      });
  },

  load(path) {
    const url = this.url(path, this.sectionId);
    const key = url.toString();
    if (!this.pages.has(key)) {
      const read = () =>
        this.fetchSource(url, '[data-result-count-source]').then((source) => {
          const counts = new Map();
          const groups = new Set();
          source.querySelectorAll('[data-count]').forEach((item) => {
            groups.add(item.dataset.name);
            counts.set(`${item.dataset.name}=${item.dataset.value}`, Number(item.dataset.count));
          });
          return { total: Number(source.dataset.resultCountSource), counts, groups };
        });
      const page = read().catch(() => read());
      page.catch(() => this.pages.delete(key));
      this.pages.set(key, page);
    }
    return this.pages.get(key);
  },

  readCardCount(path, page, signal) {
    const url = this.url(path, this.cardCountSectionId);
    url.searchParams.set('page', page);
    return this.fetchSource(url, '[data-card-products]', signal).then((source) => ({
      cards: Number(source.dataset.cardProducts),
      pages: Number(source.dataset.pages) || 1,
    }));
  },

  cardCount(path, signal) {
    if (!this.cardCounts.has(path)) {
      const readPage = (page) =>
        this.readCardCount(path, page, signal).catch((error) => {
          if (signal && signal.aborted) throw error;
          return this.readCardCount(path, page, signal);
        });
      const count = readPage(1).then(async (first) => {
        let total = first.cards;
        for (let page = 2; page <= first.pages; page += this.cardCountBatch) {
          const size = Math.min(this.cardCountBatch, first.pages - page + 1);
          const batch = await Promise.all(Array.from({ length: size }, (_, index) => readPage(page + index)));
          batch.forEach((item) => {
            total += item.cards;
          });
        }
        return total;
      });
      count.catch(() => this.cardCounts.delete(path));
      this.cardCounts.set(path, count);
    }
    return this.cardCounts.get(path);
  },
};

const ListingCount = {
  scanPath: '',
  scanController: null,

  scanCount(path) {
    if (!this.scanController) {
      this.scanController = new AbortController();
      this.scanPath = path;
    }
    return ListingReference.cardCount(path, this.scanController.signal);
  },

  async refresh() {
    const element = document.querySelector('#ProductCount [data-exact-count-url], #ProductCount [data-card-count-url]');
    const cardCountUrl = element ? element.dataset.cardCountUrl : undefined;
    if (this.scanController && this.scanPath !== cardCountUrl) {
      this.scanController.abort();
      this.scanController = null;
    }
    if (!element) return;

    try {
      const { exactCountUrl } = element.dataset;
      const total = exactCountUrl
        ? (await ListingReference.load(exactCountUrl)).total
        : await this.scanCount(cardCountUrl);
      if (!element.isConnected) return;

      element.textContent = element.dataset.countTemplate.replace('__COUNT__', total);
      if (total !== 0) return;

      const grid = document.getElementById('ProductGridContainer');
      const emptyState = grid && grid.querySelector('template[data-listing-empty-state]');
      if (emptyState) grid.innerHTML = emptyState.innerHTML;
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Listing count error:', error);
    }
  },
};

const ListingFacetCounts = {
  async refresh() {
    const element = document.querySelector('#ProductCount [data-facet-count-url]');
    if (!element) return;

    try {
      const reference = await ListingReference.load(element.dataset.facetCountUrl);
      if (!element.isConnected) return;

      document.querySelectorAll('input[name^="filter."]').forEach((input) => {
        const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const count = label && label.querySelector('.count');
        if (!count || !reference.groups.has(input.name)) return;
        const value = reference.counts.get(`${input.name}=${input.value}`) || 0;
        count.textContent = value;
        input.disabled = value === 0 && !input.checked;
      });
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Listing facet count error:', error);
    } finally {
      if (element.isConnected) element.dataset.facetCountsReady = '';
    }
  },
};

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  const link = event.target.closest('#ProductGridContainer a[href*="/products/"]');
  if (!link) return;
  ListingScroll.save(link);
  ListingScroll.clickedAt = Date.now();
});

window.addEventListener('pagehide', () => {
  if (Date.now() - ListingScroll.clickedAt > 1000) ListingScroll.saveCurrent();
});

document.addEventListener('collection:rerendered', () => {
  ListingCount.refresh();
  ListingFacetCounts.refresh();
});

document.addEventListener('shopify:section:load', () => {
  ListingCount.refresh();
  ListingFacetCounts.refresh();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ListingCount.refresh();
    ListingFacetCounts.refresh();
    ListingScroll.restore();
  });
} else {
  ListingCount.refresh();
  ListingFacetCounts.refresh();
  ListingScroll.restore();
}

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);
